import React, { useState, useEffect, useCallback, useMemo } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  TextField,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import classnames from 'classnames';
import CloseIcon from 'assets/images/close-icon.svg'
import PencilI from "assets/images/edit-icon-pencil.svg"
import { SearchSelect } from 'lib';
import styles from './styles';

// Try to make it as a reusable component
const HICodes = ({
  classes,
  value,
  changeValue,
  handleBlur,
  errors,
  touched,
  openSnackbar,
  disabled,
  options,
  loading,
  createAction,
  editAction,
  deleteAction,
}) => {
  const snackBarInfo = useSelector((state) => state.people.snackBarInfo);

  const [inputValue, setInputValue] = useState({ name: '' });

  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isItemDeleted, setIsItemDeleted] = useState(false);

  useEffect(() => {
    if (snackBarInfo) {
      openSnackbar(snackBarInfo.type, snackBarInfo.text);
    }
  }, [snackBarInfo]);

  useEffect(() => {
    if (!value || loading) return;
    const valueOption = options.find(code => code.name === value);

    if (valueOption) {
      setInputValue(valueOption);

      setIsItemDeleted(false);
      setIsCreate(false);
      setIsEdit(false);
    } else if (!disabled) {
      setIsCreate(true);
      setIsEdit(false);
      setIsItemDeleted(true);

      setInputValue({ name: value });
    }
  }, [options, value, loading, disabled])

  const handleChangeInput = (e) => {
    const value = e.target.value;
    setInputValue((prev) => ({ ...prev, name: value }));
  };

  const cancelEditCreate = (e) => {
    setIsCreate(false);
    setIsEdit(false);
    setIsItemDeleted(false);
  }


  // Add
  const addItem = () => {
    setInputValue({ name: '' });

    setIsCreate(true);
  };

  const onCreate = (e) => {
    Promise.resolve(createAction(inputValue)).then(() => {
      setIsCreate(false);
      setIsItemDeleted(false);
    });

    changeValue(inputValue.name);
  }


  // Edit
  const editItem = useCallback((_id) => (e) => {
    const item = options.find(code => code._id === _id);
    if (!item) return;

    setIsEdit(true);
    setInputValue(item);
  }, [options])

  const onEdit = (e) => {
    Promise.resolve(editAction((inputValue))).then(() => {
      setIsEdit(false);
    });
    
    changeValue(inputValue.name);
  };


  // Delete
  const onDelete = useCallback((_id) => (e) => {
    e.stopPropagation();

    Promise.resolve(deleteAction(_id)).then(() => {
      if (value.name === inputValue.name) {
        setIsItemDeleted(true);
      }
    });
  }, [inputValue, value]);


  // a read-only field must still show a code that is no longer in the list
  const shownOptions = useMemo(
    () => (!disabled || !value || options.some((code) => code.name === value)
      ? options
      : [...options, { _id: value, name: value }]),
    [disabled, value, options],
  );

  const renderCode = useCallback((code) => (
    <div className={classes.row}>
      <span className={classes.rowLabel}>{code.name}</span>
      {!disabled && (
        <div className={classes.rowActions}>
          <Button
            color="primary"
            className={classes.iconButton}
            onClick={editItem(code._id)}
            disableRipple
            aria-label="edit"
          >
            <img src={PencilI} className={classes.optionsIcons} alt="edit" />
          </Button>
          <Button
            color="primary"
            className={classes.iconButton}
            onClick={onDelete(code._id)}
            disableRipple
            aria-label="delete"
          >
            <img src={CloseIcon} alt="delete" className={classes.optionsIcons} />
          </Button>
        </div>
      )}
    </div>
  ), [classes, disabled, editItem, onDelete]);

  return (
    <>
      {(isCreate || isEdit) && !disabled ? (
        <TextField
          id="HICode"
          name="HICode"
          value={inputValue.name || ''}
          onChange={handleChangeInput}
          type="text"
          className={classes.textField}
          variant="outlined"
          fullWidth
          inputProps={{ 'aria-label': 'HI Code' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <div className={classes.inlineFlex}>
                  {isEdit ? (
                    <Button
                      variant="text"
                      className={classnames(classes.inputButton, classes.primaryButton)}
                      onClick={onEdit}
                      disableRipple
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      variant="text"
                      className={classnames(classes.inputButton, classes.primaryButton)}
                      onClick={onCreate}
                      disabled={!inputValue.name}
                    >
                      Add
                    </Button>
                  )}
                  <Button
                    variant="text"
                    className={classes.inputButton}
                    onClick={cancelEditCreate}
                    disableRipple
                  >
                    Cancel
                  </Button>
                </div>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <SearchSelect
          value={value}
          onChange={(name) => changeValue(name ?? '')}
          options={shownOptions}
          getOptionId={(code) => code.name}
          renderOption={renderCode}
          footer={!disabled && (
            <Button
              variant="text"
              className={classes.addButton}
              onClick={addItem}
              disableRipple
            >
              + Add HI Code
            </Button>
          )}
          disabled={!!disabled}
          error={Boolean(errors.HICode) && Boolean(touched.HICode)}
          classes={{ root: classes.textField, option: classes.option }}
          inputProps={{
            id: 'HICode',
            name: 'HICode',
            'aria-label': 'HI Code',
            onBlur: handleBlur,
          }}
        />
      )}
      {isItemDeleted && !disabled && (
        <Typography variant="body1" color="textSecondary">
          This Health Insurance code was deleted. Do you want to add it again?
        </Typography>
      )}
    </>
  );
};

HICodes.propTypes = {
  value: propTypes.string.isRequired,
  changeValue: propTypes.func.isRequired,
  handleBlur: propTypes.func,
  errors: propTypes.object,
  touched: propTypes.object,
  openSnackbar: propTypes.func.isRequired,
  disabled: propTypes.bool,
  createAction: propTypes.func.isRequired,
  editAction: propTypes.func.isRequired,
  deleteAction: propTypes.func.isRequired,
};

export default withStyles(styles)(HICodes);
